/**
 * Settings Menu Framework
 *
 * Provides reusable components for widget Settings menus.
 * Handles automatic menu closing for action buttons.
 *
 * Design Rules (see APP-CONTEXT.md):
 * - NO dropdowns inside menu (exception: View Mode selector)
 * - Maximum 6 items
 * - Consistent order: View Mode → Display toggles → Separator → Configure Columns → Add Symbol
 * - Action buttons auto-close menu before executing
 */

import React, { createContext, useContext, useCallback } from 'react'
import { Separator } from './separator'
import { cn } from '../../utils'

// ============================================
// CONTEXT
// ============================================

interface SettingsMenuContextValue {
  closeMenu: () => void
}

const SettingsMenuContext = createContext<SettingsMenuContextValue | null>(null)

/**
 * Hook to access the settings menu context.
 * Must be used within a SettingsMenuProvider.
 */
export function useSettingsMenu() {
  const context = useContext(SettingsMenuContext)
  if (!context) {
    throw new Error('useSettingsMenu must be used within a SettingsMenuProvider')
  }
  return context
}

// ============================================
// PROVIDER
// ============================================

interface SettingsMenuProviderProps {
  closeMenu?: () => void
  children: React.ReactNode
}

/**
 * Provider for Settings Menu context.
 * Wraps the menu content and provides closeMenu to child components.
 */
export function SettingsMenuProvider({ closeMenu, children }: SettingsMenuProviderProps) {
  // Use ref to always have latest closeMenu without re-creating context value
  const closeMenuRef = React.useRef(closeMenu)
  closeMenuRef.current = closeMenu

  const value = React.useMemo<SettingsMenuContextValue>(() => ({
    closeMenu: () => {
      closeMenuRef.current?.()
    },
  }), [])

  return (
    <SettingsMenuContext.Provider value={value}>
      {children}
    </SettingsMenuContext.Provider>
  )
}

// ============================================
// UTILITIES
// ============================================

/**
 * Wait for menu to close before executing action.
 * Uses setTimeout to ensure React has committed state changes and DOM has updated.
 *
 * Why setTimeout instead of double RAF?
 * - More reliable across different browsers and React versions
 * - Ensures the menu close animation completes
 * - 50ms is enough for React to commit and browser to paint
 */
export async function waitForMenuClose(closeMenu?: () => void): Promise<void> {
  closeMenu?.()
  // Wait for React to commit the state change and DOM to update
  await new Promise<void>(resolve => setTimeout(resolve, 50))
}

// ============================================
// COMPONENTS
// ============================================

interface SettingsMenuRootProps {
  closeMenu?: () => void
  children: React.ReactNode
  className?: string
}

/**
 * Root container for Settings Menu content.
 * Provides context and standard styling.
 */
export function SettingsMenuRoot({ closeMenu, children, className }: SettingsMenuRootProps) {
  return (
    <SettingsMenuProvider closeMenu={closeMenu}>
      <div className={cn('py-2 min-w-dropdown-lg', className)}>
        {children}
      </div>
    </SettingsMenuProvider>
  )
}

interface SettingsMenuSectionProps {
  children: React.ReactNode
  className?: string
}

/**
 * Section container for grouped settings (e.g., display toggles, view mode).
 */
export function SettingsMenuSection({ children, className }: SettingsMenuSectionProps) {
  return (
    <div className={cn('px-4 pb-2 space-y-1', className)}>
      {children}
    </div>
  )
}

/**
 * Separator between menu sections.
 */
export function SettingsMenuSeparator() {
  return <Separator className="my-1" />
}

interface SettingsMenuActionProps {
  onClick?: () => void | Promise<void>
  disabled?: boolean
  children: React.ReactNode
  className?: string
}

/**
 * Action button that auto-closes the menu before executing.
 *
 * Use this for actions that open modals or navigate away.
 * The menu will close and wait for React to commit before onClick executes.
 *
 * @example
 * <SettingsMenuAction onClick={openColumnSetup}>
 *   Configure Columns
 * </SettingsMenuAction>
 */
export function SettingsMenuAction({
  onClick,
  disabled,
  children,
  className,
}: SettingsMenuActionProps) {
  const { closeMenu } = useSettingsMenu()

  const handleClick = useCallback(async () => {
    if (disabled) return

    // Close menu and wait for React to commit state
    await waitForMenuClose(closeMenu)

    // Now execute the action (e.g., open modal)
    await onClick?.()
  }, [closeMenu, disabled, onClick])

  return (
    <button
      type="button"
      className={cn(
        'w-full text-left px-4 py-3 text-sm rounded-md',
        'hover:bg-accent transition-colors',
        disabled && 'text-muted-foreground cursor-not-allowed opacity-50',
        className
      )}
      onClick={(e) => {
        e.preventDefault()
        e.stopPropagation()
        handleClick()
      }}
      disabled={disabled}
    >
      {children}
    </button>
  )
}

// ============================================
// COMPOUND EXPORT
// ============================================

export const SettingsMenu = {
  Root: SettingsMenuRoot,
  Section: SettingsMenuSection,
  Separator: SettingsMenuSeparator,
  Action: SettingsMenuAction,
  Provider: SettingsMenuProvider,
}
